<template>
	<div class="flow-radio-wrapper" :alignItems="alignItems" :disabled="disabled">
		<div
			class="flow-radio"
			:tabindex="tabindex"
			:checked="innerChecked"
			:disabled="disabled"
			:state="state"
			:size="size"
			:data-qa-field="dataQaField"
			@click="handleClick($event)"
		>
			<svg v-if="innerChecked" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
				<path d="M6,13.223,8.45,16.7a1.049,1.049,0,0,0,1.707.051L18,6.828" />
			</svg>
		</div>
		<div class="flow-radio-label" @click="handleClick($event)">
			<!-- @slot Used to display radio button label-->
			<slot />
		</div>
	</div>
</template>
<script>
import { defineComponent } from "vue";
/**
 * The radio button allows a user to select single option out of multiple options, compared to checkbox that allows multiple selections.
 */
export default defineComponent({
	name: "RadioButton",
	props: {
		/**
		 * true | false
		 */
		checked: {
			type: Boolean,
			default: false
		},
		/**
		 * true | false
		 */
		disabled: {
			type: Boolean,
			default: false
		},
		/**
		 * tabindex number
		 *  */
		tabindex: {
			type: Number,
			default: -1
		},
		/**
		 * 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'default'
		 */
		state: {
			type: String,
			default: "primary",
			validator(value) {
				return ["primary", "secondary", "success", "error", "warning", "default"].includes(value);
			}
		},
		/**
		 * 'start' | 'center' | 'end'
		 */
		alignItems: {
			type: String,
			default: "center",
			validator(value) {
				return ["center", "start", "end"].includes(value);
			}
		},
		/**
		 * 'small' | 'medium' | 'large'
		 */
		size: {
			type: String,
			default: "small",
			validator(value) {
				return ["small", "medium", "large"].includes(value);
			}
		},
		/**
		 * QA attribute
		 */
		dataQaField: {
			type: String,
			default: null
		}
	},
	computed: {
		innerChecked: {
			// getter
			get() {
				return this.checked;
			},
			// setter
			set(newValue) {
				/**
				 * Whenever value is updated.<br/>
				 * use 'checked.sync' in parent component to achieve 2 way binding.
				 *
				 * @event update:value
				 */
				this.$emit("update:checked", newValue);
			}
		}
	},
	methods: {
		handleClick(e) {
			if (!this.disabled) {
				const currentValue = this.innerChecked;
				this.innerChecked = !currentValue;
				/**
				 * click event
				 *
				 * @event click
				 */

				this.$emit("click", e);
				/**
				 * input event
				 * @event input
				 */
				this.$emit("input", !currentValue);
			}
		}
	}
});
</script>
<style lang="scss">
@use "sass:math";

@mixin flow-radio($state-colors, $font-color, $primary, $tick) {
	div.flow-radio-wrapper {
		> div.flow-radio {
			border: 1px solid $font-color;
			&[checked] {
				@each $state, $color in $state-colors {
					&[state="#{$state}"] {
						background-color: $color;
						border: 1px solid $color;
					}
				}
			}
			@each $state, $color in $state-colors {
				@if $state == error {
					&[state="#{$state}"] {
						border: 1px solid $color;
					}
				}
			}
			> svg {
				> path {
					fill: none;
					stroke: $tick;
				}
			}
			&:not([disabled]) {
				&:focus {
					outline: none;
					&:before {
						background-color: transparent;
						border: 1px solid $primary;
					}
				}
			}
		}
		> div.flow-radio-label {
			color: $font-color;
		}
	}
}

$sizes: (
	"large": 32px,
	"medium": 24px,
	"small": 16px
);

div.flow-radio-wrapper {
	display: flex;
	// flex-direction: column;
	cursor: pointer;
	position: relative;

	&[alignItems="center"] {
		align-items: center;
	}
	&[alignItems="start"] {
		align-items: flex-start;
		> div.flow-radio {
			margin-top: 2px;
		}
	}
	&[alignItems="end"] {
		align-items: flex-end;
	}

	> div.flow-radio {
		@each $name, $size in $sizes {
			&[size="#{$name}"] {
				height: $size;
				width: $size;
				flex: 0 0 $size;
				border-radius: math.div($size, 2);

				&:not([disabled]) {
					&:focus {
						&:before {
							border-radius: math.div($size, 2) + 3px;
						}
					}
				}

				> svg {
					height: $size;
					width: $size;
				}
			}
		}

		margin-right: 12px;
		display: inline-flex;
		align-items: center;
		position: relative;
		outline: none;

		> svg {
			// height: 16px;
			// width: 16px;

			> path {
				stroke-linecap: round;
				stroke-linejoin: round;
				stroke-width: 2.5px;
			}
		}

		&:not([disabled]) {
			&:focus {
				&:before {
					content: "";
					position: absolute;
					top: -3px;
					right: -3px;
					bottom: -3px;
					left: -3px;
				}
			}
		}
	}

	> div.flow-radio-label {
		font-size: 12px;
		line-height: 16px;
		font-weight: normal;
		word-break: break-all;
	}

	&[disabled] {
		opacity: 0.5;
		cursor: not-allowed;
	}
}
div.flow-radio-wrapper + div.flow-radio-wrapper {
	margin: 8px 0px 0px 0px;
}
div.flow-form-field-label-wrapper + div.flow-radio-wrapper {
	margin: 4px 0px 0px 0px;
}
@include flow-radio(
	(
		"default": var(--dashboard-primary),
		"primary": var(--dashboard-primary),
		"secondary": var(--dashboard-secondary),
		"success": var(--dashboard-success),
		"error": var(--dashboard-danger),
		"warning": var(--dashboard-warning)
	),
	var(--dashboard-font),
	var(--dashboard-primary),
	var(--dashboard-background)
);

div.sideout-container {
	@include flow-radio(
		(
			"default": var(--slideout-primary),
			"primary": var(--slideout-primary),
			"secondary": var(--slideout-secondary),
			"success": var(--slideout-success),
			"error": var(--slideout-danger),
			"warning": var(--slideout-warning)
		),
		var(--slideout-font),
		var(--slideout-primary),
		var(--slideout-background)
	);
}
</style>
